import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import api from "@helpers/api";
import { postStore } from "@helpers/posts";
import { useAuth } from "@contexts/AuthContext";
import { useCommunity } from "@contexts/CommunityContext";

// `api` resolves with the axios error instead of throwing.
function ok(res) {
    return !!res && !res.isAxiosError && res.status >= 200 && res.status < 300;
}

export default function useAdminPostManage({ modalRef }) {
    const { t } = useTranslation("groupModals");
    const { user } = useAuth();
    const { community } = useCommunity();
    const callbackRef = useRef();
    const editModalRef = useRef();

    const [open, setOpen] = useState(false);
    const [post, setPost] = useState(null);
    const [loading, setLoading] = useState(false);

    modalRef.current = {
        open(targetPost, onChange) {
            setPost(targetPost || null);
            callbackRef.current = onChange;
            setOpen(true);
        },
    };

    function handleClose() {
        setOpen(false);
    }

    function notifyChange() {
        callbackRef.current?.();
    }

    // Only the author may edit or publish a scheduled post (the API enforces
    // it too); both ids are the hashed public user id.
    const isScheduled = !!post?.scheduled_to;
    const isAuthor = !!user?.id && !!post?.publisher?.id && user.id === post.publisher.id;
    const canManageSchedule = isScheduled && isAuthor;

    // The composer reopens in edit mode on top of this modal's place.
    function handleEdit() {
        if (!post?.id) return;

        setOpen(false);
        editModalRef.current?.open(
            { community: community.get(post.community?.id), post },
            () => notifyChange()
        );
    }

    function handlePostNow() {
        if (!post?.id) return;

        window.openConfirm({
            title: t("adminPostManage.postNow.title"),
            headline: t("adminPostManage.postNow.headline"),
            subtitle: t("adminPostManage.postNow.subtitle"),
            buttons: [
                {
                    text: t("adminPostManage.postNow.confirm"),
                    onClick: executePostNow,
                    variant: "contained",
                    className: "",
                },
                {
                    text: t("adminPostManage.postNow.keep"),
                    variant: "text",
                },
            ],
        });

        async function executePostNow() {
            setLoading(true);
            try {
                // `scheduled_to: null` = publish now; the member e-mail (if asked
                // for) goes out on the scheduler's next tick.
                const res = await api.patch(`community/group/post/${post.id}`, {
                    scheduled_to: null,
                });

                if (ok(res) && res.data?.id) {
                    postStore.set(res.data);
                    window.setSnackbar(t("adminPostManage.postNow.success"));
                    setOpen(false);
                    notifyChange();
                } else {
                    window.setSnackbar(t("adminPostManage.postNow.error"));
                }
            } catch (e) {
                window.setSnackbar(t("adminPostManage.postNow.error"));
            } finally {
                setLoading(false);
            }
        }
    }

    async function handleAddPoints(points) {
        if (!post?.id) return false;

        if (points < 1 || points > 10000) {
            window.setSnackbar(
                t("adminPostManage.addPoints.rangeError")
            );
            return false;
        }

        setLoading(true);
        try {
            const { data } = await api.post(
                `community/group/post/${post.id}/points`,
                { points }
            );

            if (data.success) {
                window.setSnackbar(t("adminPostManage.addPoints.success"));
                setOpen(false);
                notifyChange();
                return true;
            }

            window.setSnackbar(
                t("adminPostManage.addPoints.error")
            );
        } catch (e) {
            window.setSnackbar(
                t("adminPostManage.addPoints.unexpectedError")
            );
        } finally {
            setLoading(false);
        }

        return false;
    }

    async function handleDeducePoints(points) {
        if (!post?.id) return false;

        const available = post?.insights?.points || 0;

        if (points < 1 || points > available) {
            window.setSnackbar(
                t("adminPostManage.deducePoints.rangeError", { available })
            );
            return false;
        }

        setLoading(true);
        try {
            const { data } = await api.delete(
                `community/group/post/${post.id}/points`,
                { points }
            );

            if (data.success) {
                window.setSnackbar(t("adminPostManage.deducePoints.success"));
                setOpen(false);
                notifyChange();
                return true;
            }

            window.setSnackbar(
                t("adminPostManage.deducePoints.error")
            );
        } catch (e) {
            window.setSnackbar(
                t("adminPostManage.deducePoints.unexpectedError")
            );
        } finally {
            setLoading(false);
        }

        return false;
    }

    function handleDeletePost() {
        if (!post?.id) return;

        window.openConfirm({
            title: t("adminPostManage.deletePost.title"),
            headline: t("adminPostManage.deletePost.headline"),
            subtitle: t("adminPostManage.deletePost.subtitle"),
            buttons: [
                {
                    text: t("adminPostManage.deletePost.confirm"),
                    onClick: executeDelete,
                    variant: "contained",
                    className: "",
                    color: "error",
                },
                {
                    text: t("adminPostManage.deletePost.keep"),
                    variant: "text",
                },
            ],
        });

        async function executeDelete() {
            try {
                const { data } = await api.delete(
                    `community/group/post/${post.id}`
                );

                if (data.success) {
                    window.setSnackbar(t("adminPostManage.deletePost.success"));
                    setOpen(false);
                    notifyChange();
                } else {
                    window.setSnackbar(
                        t("adminPostManage.deletePost.error")
                    );
                }
            } catch (e) {
                window.setSnackbar(
                    t("adminPostManage.deletePost.unexpectedError")
                );
            }
        }
    }

    return {
        open,
        post,
        loading,
        editModalRef,
        canManageSchedule,
        handleClose,
        handleEdit,
        handlePostNow,
        handleAddPoints,
        handleDeducePoints,
        handleDeletePost,
    };
}
